import type { AnyRawTransaction } from '@aptos-labs/ts-sdk';
import { getDefaultClient } from '@dynamic-labs-sdk/client';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';

import type { AptosWalletAccount } from '../AptosWalletAccount.types';
import type { AptosSignTransactionOutput } from '../AptosWalletProvider.types';
import { NotAptosProviderError } from '../errors/NotAptosProviderError';
import { isAptosWalletProvider } from '../isAptosWalletProvider';

type SignTransactionParams = {
  asFeePayer?: boolean;
  transaction: AnyRawTransaction;
  walletAccount: AptosWalletAccount;
};

/**
 * Signs an Aptos transaction
 * @param params.transaction - The transaction to sign
 * @param params.walletAccount - The wallet account to sign the transaction with
 * @param params.asFeePayer - Whether to sign as the fee payer
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 *
 * @returns An object containing the signature output
 */
export const signTransaction = async (
  { asFeePayer, transaction, walletAccount }: SignTransactionParams,
  client = getDefaultClient()
): Promise<{ signature: AptosSignTransactionOutput }> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isAptosWalletProvider(provider)) {
    throw new NotAptosProviderError({ providerKey: provider.key });
  }

  return provider.signTransaction({ asFeePayer, transaction, walletAccount });
};
